// Shared types for the call engine: the state it publishes to the UI and the
// interfaces it requires from the host app (identity, storage, network). All
// host capabilities are injected so this module stays framework- and
// platform-independent — React subscribes via the Store, J7's identity/storage
// modules plug in through these interfaces at integration time.

import type { EndpointList, IceServer } from '@merkury/protocol';

// ---------------------------------------------------------------------------
// Injected host interfaces (dependency injection boundary with J7)
// ---------------------------------------------------------------------------

/** The device identity, provided by src/identity (J7). Signs raw bytes. */
export interface IdentitySigner {
  /** Own account UUID. */
  readonly userId: string;
  /** Own raw Ed25519 public key, base64 (key fingerprints, safety numbers). */
  readonly publicKeyB64: string;
  /** Ed25519-sign arbitrary bytes with the identity private key. */
  sign(data: Uint8Array): Promise<Uint8Array>;
}

/** Friend directory lookups, provided by the contacts layer. */
export interface ContactsProvider {
  /** Base64 raw Ed25519 public key for an accepted friend, or null. */
  getPublicKey(userId: string): Promise<string | null>;
}

/** Persistent key-value slice for TOFU key pins (J7 storage). */
export interface KeyPinStorage {
  get(userId: string): Promise<string | null>;
  set(userId: string, publicKeyB64: string): Promise<void>;
  delete(userId: string): Promise<void>;
}

/** Persisted endpoint-list state (last verified list + last-known-good URL). */
export interface EndpointListStorage {
  load(): Promise<{ list: EndpointList; lastGoodUrl?: string } | null>;
  save(state: { list: EndpointList; lastGoodUrl?: string }): Promise<void>;
}

export interface AccessTokenOptions {
  /**
   * Bypass any cached access token: refresh, and failing that mint a new
   * session from the stored identity, before returning. Set by the signaling
   * client after the server REFUSED the session (WS 4001/4403) — the cached
   * token is known-dead there, so re-sending it can only fail again. Providers
   * that ignore the hint still recover, but only once their cached token
   * expires on its own; honouring it turns minutes of deafness into seconds.
   */
  forceFresh?: boolean;
}

/** Auth-token supplier (J7 auth): a currently-valid JWT access token. */
export type AccessTokenProvider = (opts?: AccessTokenOptions) => Promise<string>;

/** TURN credential supplier (REST `POST /turn/credentials`, wired by host). */
export type IceServersProvider = () => Promise<IceServer[]>;

// ---------------------------------------------------------------------------
// Published state (what React subscribes to)
// ---------------------------------------------------------------------------

export type SignalingStatus =
  | 'idle' // not asked to connect yet (only stop() lands here)
  | 'connecting' // racing endpoints / authenticating
  | 'connected' // authed, heartbeat healthy
  | 'reconnecting' // was connected, lost the socket, retrying
  /**
   * The server refused several freshly-minted sessions in a row: report the
   * session as terminated. NOT terminal — the client keeps probing on a slow
   * cadence, and immediately on any foreground/wake/"retry now" nudge, because
   * only the server can know whether the account is still refused (see
   * signaling/client.ts sessionRejected).
   */
  | 'revoked';

export type CallPhase =
  | 'idle'
  | 'dialing' // outgoing invite sent, no ringing confirmation yet
  | 'ringing_remote' // outgoing, callee device is ringing
  | 'ringing' // incoming invite, ringing locally
  | 'connecting' // accepted, ICE in progress
  | 'in_call'
  | 'reconnecting'; // ICE lost mid-call, restart in progress

export type CallDirection = 'outgoing' | 'incoming';

/** Why the last call ended — local vocabulary, richer than the wire enum. */
export type CallEndCause =
  | 'hangup' // either side hung up normally
  | 'declined'
  | 'busy'
  | 'no_answer' // outgoing ring timed out
  | 'missed' // incoming ring timed out
  | 'answered_elsewhere'
  | 'connection_lost' // reconnect budget exhausted / RTP timeout
  | 'failed' // never connected (ICE setup failure)
  | 'mic_denied' // user rejected the microphone permission
  | 'security'; // SDP signature verification failed

/** Degradation ladder rung, docs/01-architecture.md §5. */
export type LadderRung = 0 | 1 | 2 | 3 | 4 | 5;

/** Connection quality bar shown in the in-call UI. */
export type QualityBar = 'green' | 'yellow' | 'red';

export interface ActiveCallState {
  callId: string;
  peerUserId: string;
  direction: CallDirection;
  phase: CallPhase;
  /** Set when ICE first connects; drives the call timer. */
  connectedAt: number | null;
  /** Set when phase becomes `reconnecting`; drives the banner. */
  reconnectingSince: number | null;
  muted: boolean;
  speaker: boolean;
  /** In-call route request; AUTO = device priority (BT > wired > earpiece). */
  audioRequest: AudioRouteRequest;
  rung: LadderRung;
  bar: QualityBar;
  /** True when the current path is a TURN relay. */
  viaRelay: boolean;
}

/** Manual audio-route request from the in-call control (native only). */
export type AudioRouteRequest = 'AUTO' | 'EARPIECE' | 'SPEAKER' | 'BLUETOOTH';

/** What the audio actually plays on, as reported by the native side. */
export interface AudioRouteState {
  route: 'EARPIECE' | 'SPEAKER' | 'BLUETOOTH' | 'WIRED_HEADSET';
  bluetoothAvailable: boolean;
}

export interface EndedCallState {
  callId: string;
  peerUserId: string;
  direction: CallDirection;
  cause: CallEndCause;
  endedAt: number;
  /**
   * When ICE first connected during this call, or null if it never did. The
   * authoritative "did it connect" signal for call history — carried on the
   * ended state itself so classification never has to reconstruct it from a
   * prior snapshot (which drops when `call` is cleared before `lastEnded` is
   * set, mislabelling a completed call as declined/failed).
   */
  connectedAt: number | null;
}

/** Key-change interlock: call setup is blocked until the user decides. */
export interface KeyChangeAlert {
  peerUserId: string;
  pinnedKeyB64: string;
  newKeyB64: string;
}

export interface CallEngineState {
  signaling: SignalingStatus;
  call: ActiveCallState | null;
  /**
   * The peer's audio, published when the remote track arrives (initial
   * connect; refreshed after ICE restarts/renegotiations that replace it)
   * and reset to null on call teardown. SHELL CONTRACT: subscribe to the
   * store and on change attach it to the playback sink —
   * `audioEl.srcObject = state.remoteStream` (a real MediaStream in the
   * browser), then `audioEl.play()` from a user gesture (accept tap) per the
   * iOS rule in media/control.ts.
   */
  remoteStream: RemoteStreamLike | null;
  /** Last ended call (for "call back" UI); cleared on next dial. */
  lastEnded: EndedCallState | null;
  keyChange: KeyChangeAlert | null;
  endpointListVersion: number;
  /** Native audio-route feedback; null on web (route follows the browser). */
  audio: AudioRouteState | null;
}

/**
 * Structurally a MediaStream in the browser; typed minimally so the engine
 * stays platform-independent. The shell may cast to MediaStream.
 */
export interface RemoteStreamLike {
  getAudioTracks(): Array<{ enabled: boolean }>;
}

export const INITIAL_ENGINE_STATE: CallEngineState = {
  signaling: 'idle',
  call: null,
  remoteStream: null,
  lastEnded: null,
  keyChange: null,
  endpointListVersion: 0,
  audio: null,
};

// ---------------------------------------------------------------------------
// Minimal WebSocket surface (injectable so tests can mock the transport)
// ---------------------------------------------------------------------------

export interface WebSocketLike {
  readonly readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: { code: number; reason: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
}

export type WebSocketFactory = (url: string) => WebSocketLike;

/** Injectable clock/timer surface so all budgets are testable with fake time. */
export interface TimerHost {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
  setInterval(fn: () => void, ms: number): unknown;
  clearInterval(handle: unknown): void;
}

export const REAL_TIMERS: TimerHost = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (h) => clearTimeout(h as Parameters<typeof clearTimeout>[0]),
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: (h) => clearInterval(h as Parameters<typeof clearInterval>[0]),
};
